import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'

import { db, tref } from '../lib/firebase.js'
import CLUB from '../config/club.js'
import { PBCC_PREMIUM } from '../config/siteMode.js'
import PhotoSlideshow from './PhotoSlideshow.jsx'

// TEST/premium: deep pine wall + gold title; club/live keep the black wall.
const WALL_BG = PBCC_PREMIUM ? 'var(--tv-bg-green, #0E281D)' : '#000'
const WALL_GOLD = PBCC_PREMIUM ? 'var(--ge-gold)' : CLUB.accentColor
import { approvedPhotos, recentWindow } from '../lib/photoCuration.js'

// TV rolls on a sliding recent window, not a hard top-of-hour cutoff — so a
// photo uploaded late in an hour keeps fair screen time into the next stretch.
// Backfilled to MIN_ON_WALL so a quiet stretch never empties the wall.
const TV_WINDOW_MS = 90 * 60_000
const MIN_ON_WALL = 40

// Passive TV photo wall — the auto-advancing slideshow with NO tab chrome.
//   /tv/photos, /tv/split (TvScreen kind='photos'), the admin monitor tile,
//   and /slideshow (the phone "Play" full-screen).
// The interactive, tabbed browse lives in PhotoBrowse.jsx (mounted at
// /photowall). Both read the same approved photos; ordering + labels come from
// PhotoSlideshow (buildTvRotation).
//
//   tournamentIdProp — pin without a URL param (TV auto-pick / admin monitor).
//   embedded         — fit a parent-sized container instead of the viewport.
export default function PhotoWallApp({ tournamentIdProp = null, embedded = false } = {}) {
  const [params] = useSearchParams()
  const tournamentId = tournamentIdProp ?? params.get('t')

  const [tournament, setTournament] = useState(null)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!tournamentId) {
      setError('Missing tournament ID. Scan the photo wall QR.')
      return
    }
    const off = onValue(tref(`tournaments/${tournamentId}`), (snap) => {
      const v = snap.val()
      if (!v) setError('Tournament not found.')
      else setTournament(v)
    })
    return () => off()
  }, [tournamentId])

  const photos = useMemo(
    () => recentWindow(approvedPhotos(tournament?.photos), { now: Date.now(), windowMs: TV_WINDOW_MS, minCount: MIN_ON_WALL }),
    [tournament],
  )

  const wrapperClass = embedded ? 'w-full h-full relative overflow-hidden rounded-xl' : 'min-h-full'
  return (
    <div className={wrapperClass} style={{ backgroundColor: WALL_BG, fontFamily: 'Marcellus, Georgia, serif' }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Marcellus&display=swap');`}</style>
      {error ? (
        <FullScreenMessage embedded={embedded}>
          <div className="text-3xl mb-2">⚠️</div>
          <p>{error}</p>
        </FullScreenMessage>
      ) : !tournament ? (
        <FullScreenMessage embedded={embedded}><p>Loading…</p></FullScreenMessage>
      ) : photos.length === 0 ? (
        <EmptyWall embedded={embedded} />
      ) : (
        <PhotoSlideshow
          photos={photos}
          tournament={tournament}
          tournamentId={tournamentId}
          videoUnmuted={!!tournament?.photoSettings?.videoUnmuted}
          embedded={embedded}
        />
      )}
    </div>
  )
}

function FullScreenMessage({ children, embedded }) {
  return (
    <div className={`${embedded ? 'h-full' : 'min-h-screen'} flex items-center justify-center text-white text-center p-12`}>
      <div>{children}</div>
    </div>
  )
}

function EmptyWall({ embedded }) {
  return (
    <div className={`${embedded ? 'h-full' : 'min-h-screen'} flex flex-col items-center justify-center text-center p-6 text-white`}>
      <div className="p-6 mb-6">
        <img src={CLUB.logoLight} alt={CLUB.name} style={{ height: '160px', objectFit: 'contain' }} />
      </div>
      <h1 className="text-3xl font-extrabold tracking-widest uppercase" style={{ color: WALL_GOLD }}>
        📸 Photo Wall
      </h1>
      <p className="text-white/60 mt-8 text-base max-w-lg">
        Photos will appear here as they are approved during the tournament.
      </p>
    </div>
  )
}
